'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/shared/components/Button';
import VideoCarousel from '@/shared/components/VideoCarousel';
import { CareerJob as Job } from '@/features/careers';
import { logger } from '@/core/logger';

function getDepartmentIcon(department: string) {
	const departmentLower = department.toLowerCase();
	if (
		departmentLower.includes('engineering') ||
		departmentLower === 'engineering'
	) {
		return (
			<svg
				className="w-4 h-4"
				fill="none"
				stroke="currentColor"
				viewBox="0 0 24 24"
			>
				<path
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeWidth={2}
					d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
				/>
			</svg>
		);
	}
	if (
		departmentLower.includes('ml') ||
		departmentLower.includes('ai') ||
		departmentLower === 'ai/ml'
	) {
		return (
			<svg
				className="w-4 h-4"
				fill="none"
				stroke="currentColor"
				viewBox="0 0 24 24"
			>
				<path
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeWidth={2}
					d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
				/>
			</svg>
		);
	}
	if (departmentLower.includes('design') || departmentLower === 'design') {
		return (
			<svg
				className="w-4 h-4"
				fill="none"
				stroke="currentColor"
				viewBox="0 0 24 24"
			>
				<path
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeWidth={2}
					d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
				/>
			</svg>
		);
	}
	if (
		departmentLower.includes('devops') ||
		departmentLower.includes('infrastructure') ||
		departmentLower === 'infrastructure'
	) {
		return (
			<svg
				className="w-4 h-4"
				fill="none"
				stroke="currentColor"
				viewBox="0 0 24 24"
			>
				<path
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeWidth={2}
					d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
				/>
			</svg>
		);
	}
	return null;
}

export default function CareersPage() {
	const [jobs, setJobs] = useState<Job[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		async function fetchJobs() {
			try {
				const response = await fetch('/api/careers/jobs');
				const result = await response.json();
				logger.info('[CareersPage] API response', result);
				if (result.success && result.data) {
					setJobs(result.data);
				} else {
					logger.error(
						`[CareersPage] API error: ${result.error?.message}`
					);
				}
			} catch (error) {
				logger.error('[CareersPage] Failed to fetch jobs', error);
			} finally {
				setLoading(false);
			}
		}
		fetchJobs();
	}, []);
	return (
		<div className="min-h-screen overflow-hidden">
			{/* Fullscreen Video Background */}
			<div className="fixed inset-0 z-0">
				<VideoCarousel />
			</div>

			{/* Navigation */}
			<nav className="glass-dark fixed top-0 left-0 right-0 z-50 border-b border-white/10">
				<div className="container mx-auto px-4 py-4 flex justify-between items-center">
					<Link href="/" className="flex items-center">
						<Image
							src="/ts-logo2-v2.png"
							alt="TabStudio Logo"
							width={160}
							height={53}
							className="h-12 w-auto"
							loading="lazy"
							unoptimized
						/>
					</Link>
					<div className="flex items-center gap-4">
						<Link href="/login" prefetch={false}>
							<Button
								variant="ghost"
								size="sm"
								className="glass-light glass-hover text-white border-white/20"
							>
								Sign In
							</Button>
						</Link>
						<Link href="/register" prefetch={false}>
							<Button
								variant="default"
								size="sm"
								className="glass-frosted glass-hover bg-white/90 text-black font-semibold border-white/30"
							>
								Get Started
							</Button>
						</Link>
					</div>
				</div>
			</nav>

			<main className="relative z-10 pt-24">
				{/* Hero Section */}
				<section className="py-24 px-4">
					<div className="container mx-auto text-center">
						<h1 className="text-5xl md:text-7xl font-bold mb-6">
							Join Our
							<br />
							<span className="bg-gradient-to-r from-red-500 to-red-700 bg-clip-text text-transparent">
								Team
							</span>
						</h1>
						<p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
							Help us build the future of AI-powered video
							creation. Work with a talented team on cutting-edge
							technology.
						</p>
					</div>
				</section>

				{/* Benefits Section */}
				<section className="py-16 px-4 bg-card/30 backdrop-blur-sm">
					<div className="container mx-auto">
						<h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
							Why Join TabStudio?
						</h2>
						<div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-primary to-primary-dark rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M13 10V3L4 14h7v7l9-11h-7z"
										/>
									</svg>
								</div>
								<h3 className="text-xl font-semibold mb-3">
									Cutting-Edge Tech
								</h3>
								<p className="text-muted-foreground">
									Work with the latest AI models and
									technologies. Solve challenging problems at
									the intersection of AI and video.
								</p>
							</div>

							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-secondary to-secondary-dark rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
										/>
									</svg>
								</div>
								<h3 className="text-xl font-semibold mb-3">
									Remote-First
								</h3>
								<p className="text-muted-foreground">
									Work from anywhere in the world. We embrace
									flexibility and async collaboration for a
									global team.
								</p>
							</div>

							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-accent to-accent-dark rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
										/>
									</svg>
								</div>
								<h3 className="text-xl font-semibold mb-3">
									Competitive Pay
								</h3>
								<p className="text-muted-foreground">
									Industry-leading salaries and equity. We
									value our team and compensate fairly for
									your expertise.
								</p>
							</div>

							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-primary-dark to-secondary rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
										/>
									</svg>
								</div>
								<h3 className="text-xl font-semibold mb-3">
									Learning & Growth
								</h3>
								<p className="text-muted-foreground">
									Continuous learning opportunities with
									conference budgets, courses, and mentorship
									programmes.
								</p>
							</div>

							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-secondary-dark to-accent rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
										/>
									</svg>
								</div>
								<h3 className="text-xl font-semibold mb-3">
									Health & Wellness
								</h3>
								<p className="text-muted-foreground">
									Comprehensive health insurance, mental
									health support, and generous time off
									policies.
								</p>
							</div>

							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-accent to-primary rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
										/>
									</svg>
								</div>
								<h3 className="text-xl font-semibold mb-3">
									Great Culture
								</h3>
								<p className="text-muted-foreground">
									Collaborative environment where
									everyone&apos;s voice matters. Regular team
									events and open communication.
								</p>
							</div>
						</div>
					</div>
				</section>

				{/* Open Positions */}
				<section className="py-16 px-4">
					<div className="container mx-auto max-w-5xl">
						<h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
							Open Positions
						</h2>
						{loading ? (
							<div className="text-center py-12">
								<p className="text-muted-foreground">
									Loading jobs...
								</p>
							</div>
						) : jobs.length === 0 ? (
							<div className="text-center py-12">
								<p className="text-muted-foreground">
									No open positions at the moment. Check back
									soon!
								</p>
							</div>
						) : (
							<div className="space-y-4">
								{jobs.map((job) => (
									<div
										key={job.id}
										className="p-6 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm hover:border-primary/50 transition-all"
									>
										<div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
											<div className="flex-1">
												<h3 className="text-xl font-semibold mb-2">
													{job.title}
												</h3>
												<div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
													{job.department && (
														<span className="flex items-center gap-1">
															{getDepartmentIcon(
																job.department
															)}
															{job.department}
														</span>
													)}
													{job.location && (
														<span className="flex items-center gap-1">
															<svg
																className="w-4 h-4"
																fill="none"
																stroke="currentColor"
																viewBox="0 0 24 24"
															>
																<path
																	strokeLinecap="round"
																	strokeLinejoin="round"
																	strokeWidth={
																		2
																	}
																	d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
																/>
																<path
																	strokeLinecap="round"
																	strokeLinejoin="round"
																	strokeWidth={
																		2
																	}
																	d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
																/>
															</svg>
															{job.location}
														</span>
													)}
													{job.type && (
														<span>{job.type}</span>
													)}
												</div>
											</div>
											<Link href={`/careers/${job.slug}`}>
												<Button
													variant="outline"
													className="glass-light glass-hover border-2 border-white/30 text-white font-semibold"
												>
													Apply Now
												</Button>
											</Link>
										</div>
									</div>
								))}
							</div>
						)}
					</div>
				</section>

				{/* CTA Section */}
				<section className="py-24 px-4 bg-card/30 backdrop-blur-sm">
					<div className="container mx-auto">
						<div className="glass-gradient glass-hover relative max-w-4xl mx-auto text-center p-12 rounded-3xl border border-white/20 overflow-hidden shadow-2xl">
							<div className="relative z-10">
								<h2 className="text-4xl md:text-5xl font-bold mb-6">
									Don&apos;t See Your Role?
								</h2>
								<p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
									We&apos;re always looking for talented
									people. Send us your CV and let us know how
									you can contribute.
								</p>
								<Button
									variant="default"
									size="lg"
									className="glass-frosted glass-hover bg-white/90 text-black font-semibold border-white/30"
								>
									Send General Application
								</Button>
							</div>
						</div>
					</div>
				</section>
			</main>

			{/* Footer */}
			<footer className="border-t border-border/50 bg-card/30 backdrop-blur-sm">
				<div className="container mx-auto px-4 py-12">
					<div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
						<div>
							<h4 className="font-semibold mb-4">Product</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/features">Features</Link>
								</li>
							</ul>
						</div>
						<div>
							<h4 className="font-semibold mb-4">Company</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/about">About</Link>
								</li>
								<li>
									<Link href="/careers">Careers</Link>
								</li>
							</ul>
						</div>
						<div>
							<h4 className="font-semibold mb-4">Resources</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/help">Help Center</Link>
								</li>
								<li>
									<Link href="/api">API</Link>
								</li>
							</ul>
						</div>
						<div>
							<h4 className="font-semibold mb-4">Legal</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/privacy">Privacy</Link>
								</li>
								<li>
									<Link href="/terms">Terms</Link>
								</li>
								<li>
									<Link href="/security">Security</Link>
								</li>
							</ul>
						</div>
					</div>
					<div className="pt-8 border-t border-border/50 text-center text-sm text-muted-foreground">
						<p>
							&copy; 2025 TabStudio. Powered by AI. Built for
							creators.
						</p>
					</div>
				</div>
			</footer>
		</div>
	);
}
